import React from "react";
import { Edit2, Trash2, HelpCircle, Shield } from "lucide-react";
import type { Position } from "../../types/depth-chart";
import type { Player } from "../../types/index";
import { InjuryBadge } from "../ui/InjuryBadge";

interface DepthChartEntry {
  id?: string;
  team_id: string;
  player_id: string | null;
  position: string;
  depth: number;
  unit: string;
  // No special_teams_unit needed - formation field handles Special Teams
  color?: string;
}

interface DepthChartMobileProps {
  positions: Position[];
  depthChart: DepthChartEntry[];
  players: Player[];
  activeTab: string;
  activeDepth: number;
  depthStrings: { id: number; name: string; active: boolean }[];
  editingPosition: string | null;
  editedPositionName: string;
  setEditedPositionName: (name: string) => void;
  savePositionEdit: () => void;
  showTooltip: string | null;
  setShowTooltip: (key: string | null) => void;
  deleteCustomPosition: (position: Position) => void;
  handlePositionEdit: (position: Position) => void;
  handlePlayerChange: (
    position: string,
    depth: number,
    playerId: string | null,
    inning?: number
  ) => void;
  coloringPosition: { position: string; depth: number } | null;
  setColoringPosition: (
    pos: { position: string; depth: number } | null
  ) => void;
  handlePositionColorChange: (
    positionKey: string,
    depth: number,
    color: string,
    inning?: number
  ) => void;
  currentInning?: number;
  isBaseballFielding?: boolean;
  playerSortBy: "firstName" | "lastName" | "number";
  editedPositionFullName?: string;
  setEditedPositionFullName?: (name: string) => void;
  cancelPositionEdit?: () => void;
}

const DepthChartMobile: React.FC<DepthChartMobileProps> = ({
  positions,
  depthChart,
  players,
  activeTab,
  activeDepth,
  depthStrings,
  editingPosition,
  editedPositionName,
  setEditedPositionName,
  savePositionEdit,
  showTooltip,
  setShowTooltip,
  deleteCustomPosition,
  handlePositionEdit,
  handlePlayerChange,
  coloringPosition,
  setColoringPosition,
  handlePositionColorChange,
  currentInning = 1,
  isBaseballFielding = false,
  playerSortBy,
  editedPositionFullName = "",
  setEditedPositionFullName = () => {},
  cancelPositionEdit = () => {},
}) => {
  const getPositionColor = (position: string, depth: number): string => {
    // For baseball fielding, use currentInning as the depth to search
    const actualDepth = isBaseballFielding ? currentInning : depth;
    const entry = depthChart.find(
      (e) =>
        e.position === position &&
        e.depth === actualDepth &&
        e.unit === activeTab
    );
    return entry?.color || "";
  };

  const getSelectedPlayerId = (position: string, depth: number): string => {
    // For baseball fielding, use currentInning as the depth to search
    const actualDepth = isBaseballFielding ? currentInning : depth;
    const entry = depthChart.find(
      (e) =>
        e.position === position &&
        e.depth === actualDepth &&
        e.unit === activeTab
    );
    return entry?.player_id || "";
  };

  const getSortedPlayers = () => {
    return [...players].sort((a, b) => {
      switch (playerSortBy) {
        case "firstName":
          return a.first_name.localeCompare(b.first_name);
        case "lastName":
          return a.last_name.localeCompare(b.last_name);
        case "number":
        default:
          return (a.number || 0) - (b.number || 0);
      }
    });
  };

  const formatPlayerDisplayName = (player: Player): string => {
    return `${player.first_name} ${player.last_name} (#${player.number})`;
  };

  const getSelectedPlayer = (position: string, depth: number): Player | null => {
    const playerId = getSelectedPlayerId(position, depth);
    return players.find(p => p.id === playerId) || null;
  };

  return (
    <div className="space-y-1">
      {positions.length > 0 &&
        positions.map((position) => {
          // For a regular depth chart, only display positions that match the active depth
          if (
            activeTab !== "Special Teams" &&
            !depthStrings.find((ds) => ds.id === activeDepth)?.active
          ) {
            return null;
          }
          return (
            <div
              key={`${activeTab}-${position.key}-${isBaseballFielding ? currentInning : activeDepth}`}
              className="border border-stone border-opacity-20 rounded-lg p-1.5"
            >
              <div
                className={`flex gap-1.5 ${
                  editingPosition === position.key ? "flex-col" : "items-center"
                }`}
              >
                <div>
                  <span
                    className="font-medium px-1.5 py-0.5 rounded inline-block text-sm relative cursor-pointer"
                    style={{ backgroundColor: "transparent" }}
                    onClick={() =>
                      setShowTooltip(
                        showTooltip === position.key ? null : position.key
                      )
                    }
                  >
                    {editingPosition === position.key ? (
                      <div className="space-y-1 min-w-0 flex-1">
                        <input
                          type="text"
                          value={editedPositionName}
                          onChange={(e) =>
                            setEditedPositionName(e.target.value)
                          }
                          onKeyDown={(e) => {
                            if (e.key === "Enter") savePositionEdit();
                            if (e.key === "Escape") cancelPositionEdit();
                          }}
                          className="w-full p-1 border border-stone border-opacity-20 rounded text-sm"
                          placeholder="Position abbreviation"
                          autoFocus
                        />
                        <input
                          type="text"
                          value={editedPositionFullName}
                          onChange={(e) =>
                            setEditedPositionFullName(e.target.value)
                          }
                          onKeyDown={(e) => {
                            if (e.key === "Enter") savePositionEdit();
                            if (e.key === "Escape") cancelPositionEdit();
                          }}
                          className="w-full p-1 border border-stone border-opacity-20 rounded text-xs"
                          placeholder="Full position name"
                        />
                        <div className="flex gap-1">
                          <button
                            onClick={savePositionEdit}
                            className="px-2 py-0.5 text-xs bg-primary text-white rounded"
                          >
                            Save
                          </button>
                          <button
                            onClick={cancelPositionEdit}
                            className="px-2 py-0.5 text-xs bg-gray-300 text-gray-700 rounded"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <span className="uppercase">{position.name}</span>
                    )}

                    {showTooltip === position.key && (
                      <div className="absolute left-0 top-full mt-1 bg-dark text-white text-xs rounded px-2 py-1 z-20 whitespace-nowrap">
                        {position.fullName}
                      </div>
                    )}
                  </span>
                </div>

                {editingPosition !== position.key && (
                  <>
                    <div className="flex-1 flex items-center gap-1">
                      <select
                        value={getSelectedPlayerId(
                          position.key,
                          activeTab === "Special Teams"
                            ? 1
                            : isBaseballFielding
                            ? currentInning
                            : activeDepth
                        )}
                        onChange={(e) =>
                          handlePlayerChange(
                            position.key,
                            activeTab === "Special Teams"
                              ? 1
                              : isBaseballFielding
                              ? currentInning
                              : activeDepth,
                            e.target.value || null,
                            isBaseballFielding ? currentInning : undefined
                          )
                        }
                        className="flex-1 p-0.5 text-sm border border-stone border-opacity-20 rounded min-w-0 text-black"
                        style={{
                          backgroundColor:
                            getPositionColor(
                              position.key,
                              activeTab === "Special Teams"
                                ? 1
                                : isBaseballFielding
                                ? currentInning
                                : activeDepth
                            ) || "transparent",
                          opacity: 0.8,
                        }}
                      >
                        <option value="">—</option>
                        {getSortedPlayers().map((player) => (
                          <option key={player.id} value={player.id}>
                            {formatPlayerDisplayName(player)}
                          </option>
                        ))}
                      </select>

                      {/* Player badges */}
                      {(() => {
                        const selectedPlayer = getSelectedPlayer(
                          position.key,
                          activeTab === "Special Teams"
                            ? 1
                            : isBaseballFielding
                            ? currentInning
                            : activeDepth
                        );
                        if (!selectedPlayer) return null;
                        
                        return (
                          <div className="flex items-center gap-1">
                            {selectedPlayer.playParticipation?.hasMPR && (
                              <span className="text-blue-600" title="Minimum Play Requirements">
                                <Shield className="w-3 h-3" />
                              </span>
                            )}
                            {selectedPlayer.injury_status && (
                              <InjuryBadge 
                                injuryStatus={selectedPlayer.injury_status} 
                                size="sm"
                              />
                            )}
                          </div>
                        );
                      })()}
                    </div>

                    <div className="flex space-x-1 items-center shrink-0">
                      {/* <button
                        onClick={() => {
                          handlePositionEdit(position);
                        }}
                        className={
                          position.custom ? "text-primary" : "text-stone"
                        }
                        title={
                          position.custom
                            ? "Edit custom position label"
                            : "Create custom position label"
                        }
                      >
                        <Edit2 className="w-3 h-3" />
                      </button>
                      {position.custom && (
                        <span
                          className="text-xs text-primary opacity-75"
                          title="Custom label"
                        ></span>
                      )} */}
                      <button
                        onClick={() =>
                          setColoringPosition({
                            position: position.key,
                            depth:
                              activeTab === "Special Teams"
                                ? 1
                                : isBaseballFielding
                                ? currentInning
                                : activeDepth,
                          })
                        }
                        className="text-stone opacity-50 hover:opacity-100"
                        title="Change position color"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="14"
                          height="14"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <circle cx="13.5" cy="6.5" r="2.5" />
                          <circle cx="19" cy="12" r="2.5" />
                          <circle cx="13.5" cy="17.5" r="2.5" />
                          <circle cx="6.5" cy="12" r="2.5" />
                        </svg>
                      </button>
                    </div>
                  </>
                )}
              </div>

              {coloringPosition &&
                coloringPosition.position === position.key &&
                coloringPosition.depth ===
                  (activeTab === "Special Teams"
                    ? 1
                    : isBaseballFielding
                    ? currentInning
                    : activeDepth) && (
                  <div className="mt-2 bg-white border border-stone border-opacity-20 rounded-lg p-3 shadow-sm">
                    <div className="mb-2">
                      <label className="block text-xs font-medium mb-1">
                        Position Color
                      </label>
                      <div className="flex gap-1 flex-wrap">
                        {[
                          "",
                          "#ff0000",
                          "#ff6b00",
                          "#ffcc00",
                          "#7bcc2c",
                          "#00b341",
                          "#00ccc9",
                          "#0066ff",
                          "#6e3bef",
                          "#cc00ff",
                          "#ff0099",
                        ].map((color) => (
                          <button
                            key={color}
                            type="button"
                            className={`w-6 h-6 rounded-full border ${
                              color ===
                              getPositionColor(
                                position.key,
                                activeTab === "Special Teams"
                                  ? 1
                                  : isBaseballFielding
                                  ? currentInning
                                  : activeDepth
                              )
                                ? "ring-2 ring-offset-2 ring-primary"
                                : "border-stone"
                            }`}
                            style={{ backgroundColor: color || "transparent" }}
                            onClick={() =>
                              handlePositionColorChange(
                                position.key,
                                activeTab === "Special Teams"
                                  ? 1
                                  : isBaseballFielding
                                  ? currentInning
                                  : activeDepth,
                                color,
                                isBaseballFielding ? currentInning : undefined
                              )
                            }
                          />
                        ))}
                      </div>
                    </div>
                    <div className="flex justify-end">
                      <button
                        onClick={() => setColoringPosition(null)}
                        className="px-2 py-1 text-xs border border-stone border-opacity-20 rounded-md text-dark"
                      >
                        Close
                      </button>
                    </div>
                  </div>
                )}
            </div>
          );
        })}
    </div>
  );
};

export default DepthChartMobile;
